import React from "react";
import { twMerge } from "tailwind-merge";

/**
 * @typedef {object} AlertProps
 * @property {"success" | "warning" | "error" | "info" } type - The ID of the form to render
 * @property {string} [message] - The message
 * @property {string} [title] - The title
 * @property {boolean} [showIcon] - Whether to show the icon
 */

/**
 * App component.
 * @param {AlertProps} props - props
 * @returns {React.JSX.Element} React function component
 */
function Alert({ type = "success", message, title, showIcon = false }) {
  const iconClassNames = {
    success: ["fr-text-green-400"],
    warning: ["fr-text-yellow-400"],
    error: ["fr-text-red-400"],
    info: ["fr-text-sky-400"],
  };
  const bgClasses = {
    success: ["fr-bg-green-50"],
    warning: ["fr-bg-yellow-50"],
    error: ["fr-bg-red-50"],
    info: ["fr-bg-sky-50"],
  };
  const textClassesPrimary = {
    success: ["fr-text-green-800"],
    warning: ["fr-text-yellow-800"],
    error: ["fr-text-red-800"],
    info: ["fr-text-sky-800"],
  };
  const textClassesSecondary = {
    success: ["fr-text-green-700"],
    warning: ["fr-text-yellow-700"],
    error: ["fr-text-red-700"],
    info: ["fr-text-sky-700"],
  };
  return (
    <div
      className={twMerge(
        "fr-rounded-md fr-p-2 fr-mb-4 fr-max-w-lg",
        bgClasses[type],
      )}
    >
      <div className="fr-flex">
        {showIcon && (
          <div className="fr-flex-shrink-0">
            <svg
              className={twMerge("fr-h-5 fr-w-5", iconClassNames[type])}
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        )}
        <div className="fr-ml-3">
          {title && (
            <h3
              className={twMerge(
                "fr-text-sm fr-mb-2 fr-font-medium",
                textClassesPrimary[type],
              )}
            >
              {title}
            </h3>
          )}
          <div
            data-testid="alert-message"
            className={twMerge("fr-text-sm", textClassesSecondary[type])}
          >
            <p>{message}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Alert;
